import { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, CircularProgress, Grid, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DisconnectIcon from '@mui/icons-material/LinkOff';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { getShowcases, deleteShowcase } from '@services/showcaseService';
import { checkInstagramTokenStatus, initiateInstagramAuth, getInstagramPosts, syncInstagramPosts, disconnectInstagram } from '@services/instagramService';
import { isMobileDevice, openOAuthPopup, monitorPopup } from '../../utils/mobileOAuthHandler';
import ShowcaseForm from './ShowcaseForm';
import ShowcaseCard from './ShowcaseCard';
import InstagramPostsDialog from './InstagramPostsDialog';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { Showcase } from '@app-types/models/showcase';
import type { InstagramPost } from '@app-types/models/instagram';

interface ShowcaseCreationSectionProps {
  stylistId: string;
  onShowcaseCountChange?: (count: number) => void;
}

const ShowcaseCreationSection: React.FC<ShowcaseCreationSectionProps> = ({ stylistId, onShowcaseCountChange }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [showcases, setShowcases] = useState<Showcase[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isInstagramSyncing, setIsInstagramSyncing] = useState(false);
  const [showInstagramPosts, setShowInstagramPosts] = useState(false);
  const [instagramPosts, setInstagramPosts] = useState<InstagramPost[]>([]);
  const [selectedPosts, setSelectedPosts] = useState<string[]>([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [isInstagramConnected, setIsInstagramConnected] = useState(false);
  const [isCheckingToken, setIsCheckingToken] = useState(false);
  const [isLoadingShowcases, setIsLoadingShowcases] = useState(false);
  const [selectedShowcase, setSelectedShowcase] = useState<Showcase | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [showcaseToDelete, setShowcaseToDelete] = useState<Showcase | null>(null);

  const checkInstagramConnection = useCallback(async (stylistId: string) => {
    if (!stylistId) return;

    setIsCheckingToken(true);
    try {
      const { hasToken } = await checkInstagramTokenStatus(stylistId);
      setIsInstagramConnected(hasToken ?? false);
    } catch (error) {
      console.error("Error checking Instagram connection:", error);
      setIsInstagramConnected(false);

      // Don't show error for token checking as it's silent background check
      // unless it's a credentials error that affects functionality
      if ((error as { message?: string }).message?.includes('credentials not found') ||
          (error as { response?: { data?: { message?: string } } }).response?.data?.message?.includes('credentials not found')) {
        console.warn("Instagram credentials not configured on backend");
      }
    } finally {
      setIsCheckingToken(false);
    }
  }, []);

  const fetchShowcases = useCallback(async () => {
    if (stylistId) {
      setIsLoadingShowcases(true);
      try {
        const response = await getShowcases(stylistId);
        const data = Array.isArray(response.data) ? response.data : [];
        setShowcases(data);
        // Notify parent component about showcase count change
        if (onShowcaseCountChange) {
          onShowcaseCountChange(data.length);
        }
      } catch (error) {
        console.error("Error fetching showcases:", error);
      } finally {
        setIsLoadingShowcases(false);
      }
    }
  }, [stylistId, onShowcaseCountChange]);

  useEffect(() => {
    if (stylistId) {
      checkInstagramConnection(stylistId);
      fetchShowcases();
    }
  }, [stylistId, checkInstagramConnection, fetchShowcases]);

  // Check for Instagram authentication success in URL and verify with backend
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const instagramSuccess = searchParams.get('instagram_success');

    if (instagramSuccess === 'true' && stylistId) {
      // Don't trust URL parameter alone - re-check with backend to verify actual connection
      console.log('Instagram auth redirect detected, re-checking connection status...');
      checkInstagramConnection(stylistId);

      // Clean up URL parameter after processing to prevent repeated checks
      const url = new URL(window.location.href);
      url.searchParams.delete('instagram_success');
      window.history.replaceState({}, '', url.toString());
    }
  }, [location.search, stylistId, checkInstagramConnection]);

  const handleInstagramAuth = async () => {
    if (!stylistId) return;

    setIsLoadingPosts(true);
    try {
      // Save complete state information to sessionStorage
      sessionStorage.setItem('wizard_open', 'true');
      sessionStorage.setItem('wizard_step', '3'); // Showcase step
      if (stylistId) {
        sessionStorage.setItem('wizard_stylist_id', stylistId);
        console.log('ShowcaseCreationSection: Saved stylistId to sessionStorage:', stylistId);
      }

      // For mobile popup flow, redirect to callback handler page
      const returnUrl = isMobileDevice()
        ? `${window.location.origin}/oauth-callback`
        : `${import.meta.env.VITE_BASE_URL}/dashboard?wizard=true&step=3&instagram_success=true`;

      // Log the redirect for debugging
      console.log('Redirecting to Instagram auth with return URL:', returnUrl);

      // Initiate Instagram auth with redirect URL
      const { authUrl } = await initiateInstagramAuth(stylistId, returnUrl);

      if (!authUrl) {
        throw new Error('No auth URL received from backend');
      }

      // Mobile browser: Use popup to prevent Instagram app intercept
      if (isMobileDevice()) {
        console.log('Mobile browser detected - using popup approach');

        // CRITICAL: Open popup BEFORE any async operations (iOS requirement)
        const popup = openOAuthPopup(authUrl);

        // Check if popup was blocked
        if (!popup || popup.closed) {
          console.warn('Popup was blocked by browser');
          setIsLoadingPosts(false);

          // Fallback: Navigate to intermediate page
          navigate('/oauth-redirect', {
            state: {
              authUrl: authUrl,
              returnPath: location.pathname
            }
          });
          return;
        }

        console.log('OAuth popup opened successfully');

        // Monitor popup for completion
        monitorPopup(
          popup,
          (_token: string) => {
            // Success: Token will be in URL, reload to process it
            console.log('OAuth completed successfully');
            setIsLoadingPosts(false);
            // Reload to process the callback
            window.location.reload();
          },
          (error: Error) => {
            // Error: Show message
            console.error('OAuth popup error:', error);
            setIsLoadingPosts(false);
            toast.error(t('showcase.instagramConnectionError', 'Failed to connect Instagram. Please try again.'));
          }
        );

      } else {
        // Desktop browser: Use traditional redirect (works perfectly)
        console.log('Desktop browser detected - using traditional redirect');
        window.location.href = authUrl;
      }

    } catch (error) {
      console.error("Error with Instagram integration:", error);
      setIsLoadingPosts(false);

      // Handle specific Instagram credential errors
      if ((error as { message?: string }).message?.includes('credentials not found') ||
          (error as { response?: { data?: { message?: string } } }).response?.data?.message?.includes('credentials not found')) {
        alert(t('showcase.instagramCredentialsError'));
      } else {
        alert(t('showcase.instagramConnectionError'));
      }
    }
  };

  const handleOpenInstagramPosts = useCallback(async () => {
    if (!stylistId) return;

    setIsLoadingPosts(true);
    try {
      const postsResponse = await getInstagramPosts(stylistId);
      setInstagramPosts(postsResponse.data);
      setShowInstagramPosts(true);
    } catch (error) {
      console.error("Error fetching Instagram posts:", error);

      // Handle specific Instagram credential errors
      if ((error as { message?: string }).message?.includes('credentials not found') ||
          (error as { response?: { data?: { message?: string } } }).response?.data?.message?.includes('credentials not found')) {
        alert(t('showcase.instagramCredentialsError'));
      } else {
        alert(t('showcase.instagramPostsError'));
      }
    } finally {
      setIsLoadingPosts(false);
    }
  }, [stylistId, t]);

  const handlePostSelection = (postId: string) => {
    setSelectedPosts(prev => {
      if (prev.includes(postId)) {
        return prev.filter(id => id !== postId);
      } else {
        return [...prev, postId];
      }
    });
  };

  const handleCaptionChange = useCallback((postId: string, newCaption: string) => {
    setInstagramPosts(prevPosts =>
      prevPosts.map(post =>
        post.id === postId
          ? { ...post, caption: newCaption }
          : post
      )
    );
  }, []);

  const handleSyncSelectedPosts = async (serviceTypeId: string) => {
    if (!stylistId || selectedPosts.length === 0) return;

    setIsInstagramSyncing(true);
    try {
      // Get the selected post IDs
      const postIdsToSync = selectedPosts.map(postId => postId);

      await syncInstagramPosts(stylistId, postIdsToSync, serviceTypeId);
      fetchShowcases();
      setShowInstagramPosts(false);
      setSelectedPosts([]);
    } catch (error) {
      console.error("Error syncing selected Instagram posts:", error);

      // Handle specific Instagram credential errors
      if ((error as { message?: string }).message?.includes('credentials not found') ||
          (error as { response?: { data?: { message?: string } } }).response?.data?.message?.includes('credentials not found')) {
        alert(t('showcase.instagramCredentialsError'));
      } else {
        alert(t('showcase.importError'));
      }
    } finally {
      setIsInstagramSyncing(false);
    }
  };

  const handleDisconnectInstagram = async () => {
    if (!stylistId) return;

    try {
      await disconnectInstagram(stylistId);
      setIsInstagramConnected(false);
      setShowInstagramPosts(false); // Close the Instagram posts dialog
      toast.success('Instagram disconnected successfully!');
    } catch (error) {
      console.error("Error disconnecting Instagram:", error);
      toast.error('Failed to disconnect Instagram');
    }
  };

  const handleCloseShowcaseForm = () => {
    setShowCreateForm(false);
    setSelectedShowcase(null);
    fetchShowcases();
  };

  const handleEditShowcase = (showcase: Showcase) => {
    setSelectedShowcase(showcase);
    setShowCreateForm(true);
  };

  const handleDeleteShowcase = (showcase: Showcase) => {
    setShowcaseToDelete(showcase);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!showcaseToDelete) return;

    try {
      await deleteShowcase(showcaseToDelete._id);
      toast.success(t('showcaseManagement.deleteSuccess', 'Showcase deleted successfully'));
      setIsDeleteDialogOpen(false);
      setShowcaseToDelete(null);
      fetchShowcases();
    } catch (error) {
      console.error('Error deleting showcase:', error);
      toast.error(t('showcaseManagement.deleteError', 'Failed to delete showcase'));
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('quickListingWizard.createShowcases')}
        </Typography>
        <Typography paragraph>
          {t('quickListingWizard.showcaseInstructions')}
        </Typography>

        {isLoadingShowcases ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography sx={{ mt: 2, mb: 2 }}>
              {t('dashboard.validShowcases', { count: showcases.length })}
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setShowCreateForm(true)}
              >
                {t('showcaseManagement.createShowcase')}
              </Button>

              {!isInstagramConnected ? (
                <Button
                  variant="outlined"
                  startIcon={<InstagramIcon />}
                  onClick={handleInstagramAuth}
                  disabled={isCheckingToken}
                >
                  {isCheckingToken ? (
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                  ) : null}
                  {t('showcaseManagement.connectInstagram')}
                </Button>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<CheckCircleIcon />}
                    endIcon={<InstagramIcon />}
                    onClick={handleOpenInstagramPosts}
                    disabled={isLoadingPosts}
                  >
                    {isLoadingPosts ? (
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                    ) : null}
                    {t('showcaseManagement.viewInstagramPosts')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DisconnectIcon />}
                    onClick={handleDisconnectInstagram}
                  >
                    {t('showcaseManagement.disconnectInstagram')}
                  </Button>
                </>
              )}
            </Box>

            {showcases.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {t('showcaseManagement.showcaseMedia')}
                </Typography>
                <Grid container spacing={2}>
                  {showcases.slice(0, 6).map((showcase) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={showcase._id}>
                      <ShowcaseCard
                        showcase={showcase}
                        onEdit={() => handleEditShowcase(showcase)}
                        onDelete={() => handleDeleteShowcase(showcase)}
                        showActions={true}
                        showStatus={true}
                        showContent={true}
                        contentMaxLength={60}
                        elevation={1}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </>
        )}
      </Box>

      {showCreateForm && (
        <ShowcaseForm
          selectedStylist={stylistId}
          showcase={selectedShowcase}
          closeModal={handleCloseShowcaseForm}
          isInDialog={true}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('showcaseManagement.deleteTitle', 'Delete Showcase')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('showcaseManagement.deleteConfirmation', 'Are you sure you want to delete this showcase? This action cannot be undone.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <InstagramPostsDialog
        open={showInstagramPosts}
        onClose={() => setShowInstagramPosts(false)}
        posts={instagramPosts}
        selectedPosts={selectedPosts}
        onPostSelection={handlePostSelection}
        onSync={handleSyncSelectedPosts}
        isLoading={isLoadingPosts}
        isSyncing={isInstagramSyncing}
        onCaptionChange={handleCaptionChange}
        onDisconnect={handleDisconnectInstagram}
      />
    </Box>
  );
};

export default ShowcaseCreationSection;